<template>
  <div
    id="package_details"
    class="flex-v-full">
    <div class="flex-v-full">
      <Breadcrumbs :items="breadcrumbs" />
      <div class="main-content-header">
        <h1 id="package_details_header">
          <div class="icon-label-and-actions">
            <div class="icon-and-label">
              <icon-colored-deal />
              <span>{{ deal.name }}</span>
              <span
                v-show="deal.isPrivate"
                class="icon mx-2">
                <icon-colored-private-deal />
              </span>
              <entity-id-badge :id="`${deal.id}`" />
            </div>
            <entity-actions
              :actions="actions"
              @changed="dispatchAction" />
          </div>
          <transition
            v-if="isSupplyOrganization"
            name="squish fade">
            <AvailsResult
              ref="availsResult"
              :organization-i-d="organizationId"
              :start-date="deal.startDate"
              :end-date="deal.endDate"
              :ad-duration="deal.adDurationMin"
              :budget="deal.goal === 'budget' ? deal.budgetMin : undefined"
              :budget-mode="deal.budgetMode"
              :daypart="deal.daypart"
              :demographic-i-ds="deal.demographicTargetIDs"
              :entity="deal"
              :language-i-ds="deal.languageIDs"
              :language-exclusion-i-ds="deal.languageExclusionIDs"
              :location-i-ds="deal.locationTargetIDs"
              :location-exclusion-i-ds="deal.locationExclusionIDs"
              :network-i-ds="deal.networkIDs"
              :site-i-ds="deal.siteIDs"
              :site-exclusion-i-ds="deal.siteExclusionIDs"
              :unit-i-ds="deal.unitIDs"
              :unit-exclusion-i-ds="deal.unitExclusionIDs"
              :geofence-group-i-ds="deal.geofenceGroupIDs"
              :geofence-group-exclusion-i-ds="deal.geofenceGroupExclusionIDs"
              :unit-tags="deal.unitTags"
              :unit-tag-exclusions="deal.unitTagExclusions"
              :unit-pack-i-ds="deal.unitPackIDs"
              :unit-pack-exclusion-i-ds="deal.unitPackExclusionIDs"
              :market-i-ds="deal.marketIDs"
              :priority="5"
              :creative-formats="deal.creativeFormats"
              :creative-format-exclusions="deal.creativeFormatExclusions"
              :creative-orientations="deal.creativeOrientations"
              :creative-orientation-exclusions="deal.creativeOrientationExclusions"
              :iab-categories="deal.iabCategories"
              :iab-category-exclusions="deal.iabCategoryExclusions"
              :cpm="deal.isCPMFromRateCard ? undefined : deal.cpmCalculator.cpmFixed"
              :impressions="deal.goal === 'budget' ? undefined : parseInt(deal.marketTargetedImpressionsMin, 10)"
              purchase-type="G"
              :min-seconds-between-plays="deal.minSecondsBetweenPlaysMin"
              :cadence="deal.cadenceMin" />
          </transition>
          <transition name="squish fade">
            <div
              v-if="checkAvailsAlert != null"
              class="alert alert-danger">
              {{ checkAvailsAlert }}
            </div>
          </transition>

          <DealDetailsKpis
            :deal="deal"
            :purchase-type-name="getPurchaseTypeOptionFromValue(deal.purchaseType)"
            :is-n-g-external-or-preferred-external-deal="isNGExternalOrPreferredExternalDeal || false"
            :is-supply-organization="isSupplyOrganization"
            :organization-settings="organizationSettings"
            :plan-name="planName"
            :show-package-bidding-metrics="showPackageBiddingMetrics" />
        </h1>
      </div>
      <div class="container-fluid details-container">
        <div
          v-if="hasInvalidConfiguration"
          class="row">
          <div class="col-12">
            <div class="alert alert-danger">
              <icon-warning />
              {{ capitalize(i18n.global.t('messages.dealDetailsCompatibilityError')) }}
            </div>
          </div>
        </div>

        <DealDetailsSummary
          v-if="showDealDetailsSummary"
          :is-supply-organization="isSupplyOrganization"
          :is-curator-organization="isCuratorOrganization"
          :is-exchange="isExchange"
          :deal="deal"
          :is-n-g-external-or-preferred-external-deal="isNGExternalOrPreferredExternalDeal || false" />

        <!-- SPEND STEERING -->
        <div
          v-if="showSpendSteering"
          class="row">
          <DealDetailsSpendSteering
            :suppliers="suppliers"
            :deal="deal" />
        </div>

        <div class="row">
          <!-- CO2 EMISSIONS -->
          <DealDetailsGreenScore
            v-if="deal.isCuratedDeal && deal.co2EmissionsScoreThreshold > 0"
            :deal="deal" />

          <!-- DEAL ID -->
          <DealDetailsDealID
            :deal="deal"
            :column-width="deal.isCuratedDeal ? 6 : 12"
            :height="deal.isCuratedDeal ? '11.6rem' : ''" />

          <!-- DSP / Buyers -->
          <DealDetailsDSPsAndBuyers
            v-if="
              isSupplyOrCurateOrganization &&
              !showExternalExchanges &&
              !showCuratorRestrictions &&
              !isExternalSSPCuratedDeal
            "
            :deal="deal"
            :is-external-deal="isExternalDeal"
            :package-buyer-organization-d-s-ps="packageBuyerOrganizationDSPs" />

          <!-- MEDIA OWNERS -->
          <DealDetailsMediaOwners
            v-if="(isDemandOrganization || isExchange) && mediaOwners.length > 0"
            :media-owners="mediaOwners" />

          <!-- EXCHANGE -->
          <DealDetailsExchanges
            v-if="showExternalExchanges"
            :deal="deal" />

          <!-- CURATOR RESTRICTIONS -->
          <DealDetailsCuratorRestrictions
            v-if="showCuratorRestrictions"
            :selected-curator-organizations="selectedCuratorOrganizations" />

          <!-- DATES -->
          <DealDetailsDates
            :deal="deal"
            :is-n-g-external-or-preferred-external-deal="isNGExternalOrPreferredExternalDeal || false" />

          <!-- BUDGET -->
          <!-- TODO establish if curator restrictions makes the deal external -->
          <DealDetailsBudget
            v-if="!deal.isCuratedDeal"
            :deal="deal"
            :is-supply-organization="isSupplyOrganization"
            :is-curator-organization="isCuratorOrganization"
            :is-n-g-deal="isNGDeal || false"
            :is-n-g-external-or-preferred-external-deal="
              isNGExternalOrPreferredExternalDeal || deal.curatorOrganizationIDs?.length > 0 || false
            " />

          <!-- MARKUP -->
          <DealDetailsFee
            v-if="isCuratorOrganization && deal.isCuratedDeal"
            :deal="deal" />

          <!-- PLAYBACK -->
          <DealDetailsPlayback
            v-if="!deal.isCuratedDeal"
            :deal="deal"
            :is-demand-organization="isDemandOrganization"
            :is-ad-duration-section-visible="isAdDurationSectionVisible"
            :is-n-g-external-or-preferred-external-deal="isNGExternalOrPreferredExternalDeal || false" />

          <!-- DEMOGRAPHICS -->
          <DealDetailsDemographics
            :deal="deal"
            :is-ad-duration-section-visible="isAdDurationSectionVisible"
            :is-n-g-external-or-preferred-external-deal="isNGExternalOrPreferredExternalDeal" />

          <!-- LOCATIONS -->
          <LocationTargetingDetails
            :entity="deal"
            :page-sizes="{ units: 50 }"
            :page-numbers="{ units: 0 }"
            :lengths="{ units: 0 }" />
        </div>

        <!-- DAYPART -->
        <DealDetailsDaypart
          v-if="isDaypartSectionVisible"
          :deal="deal" />

        <!-- CUSTOM PROPERTIES -->
        <DealDetailsCustomProperties :deal="deal" />

        <!-- TARGETING CRITERIA (WEATHER) -->
        <DealDetailsWeatherTargeting
          v-if="hasWeatherTargeting"
          :deal="deal"
          :targeting-criteria="targetingCriteria" />

        <!-- CUSTOM EVENT TARGETING -->
        <DealDetailsCustomEventTargeting
          :deal="deal"
          :targeting-criteria="targetingCriteria" />

        <!-- TARGETING CRITERIA (POLLEN) -->
        <div class="row">
          <PollenTargetingDetails
            v-if="hasPollenTargeting"
            :pollen-targeting-criteria="deal.packageTargetingCriteria"
            :targeting-criteria="targetingCriteria" />
        </div>

        <!-- LINE ITEMS -->
        <DealDetailsLineItems
          v-if="showLineItemsTable"
          :deal="deal" />

        <!-- EXTERNAL PACKAGES -->
        <DealDetailsExternalChildDeals
          v-if="isCuratorOrganization && deal.isCuratedDeal && deal.curationType === CurationType.EXTERNAL_SSP_DEALS"
          :deal="deal" />

        <!-- CHILD PUBLISHER PACKAGES -->
        <DealsDetailsChildDeals
          v-if="isCuratorOrganization && deal.isCuratedDeal"
          :deal="deal"
          :estimated-delivery-stats="estimatedDeliveryStats"
          :media-owners="suppliers" />

        <!-- CREATIVES -->
        <div
          v-if="!isCuratorOrganization && !isDemandOrganization"
          class="row">
          <div class="col-12">
            <DealCreativesApproval :deal="deal" />
          </div>
        </div>

        <!-- NOTES -->
        <DealDetailsNotes :deal="deal" />

        <!-- MOBILE RETARGETING -->
        <DealDetailsMobileRetargeting :deal="deal" />

        <!-- CUSTOM PROPERTIES -->
        <EntityCustomFieldsDetails
          v-if="isSupplyOrganization"
          :organization-custom-fields="organizationCustomFields"
          :entity-custom-fields="entityCustomFields" />
      </div>
    </div>
    <modal
      :visible="isEditPackageModalVisible"
      :title="capitalize(i18n.global.t('actions.editNonDraftPackage'))"
      :body="capitalize(i18n.global.t('messages.confirmEditNonDraftPackage'))"
      :button1-caption="yes"
      :button2-caption="no"
      :button2-visible="true"
      @confirmed="confirmEditPackage"
      @cancelled="cancelEditPackage" />

    <modal
      id="publish_package_confirmation_modal"
      :visible="isPublishPackageModalVisible"
      :title="capitalize(i18n.global.t('actions.publishPackage'))"
      :body="capitalize(i18n.global.t('messages.confirmPublishPackage'))"
      :button1-caption="yes"
      :button2-caption="no"
      :button2-visible="true"
      @confirmed="confirmPackagePublish"
      @cancelled="cancelPackagePublish" />

    <modal
      id="unpublish_package_confirmation_modal"
      :visible="isUnPublishPackageModalVisible"
      :title="capitalize(i18n.global.t('actions.unpublishPackage'))"
      :body="capitalize(i18n.global.t('messages.confirmUnpublishPackage'))"
      :button1-caption="yes"
      :button2-caption="no"
      :button2-visible="true"
      @confirmed="confirmPackageUnpublish"
      @cancelled="cancelPackageUnpublish" />

    <modal
      id="retire_package_confirmation_modal"
      :visible="isRetirePackageModalVisible"
      :title="capitalize(i18n.global.t('actions.retirePackage'))"
      :body="capitalize(i18n.global.t('messages.confirmRetirePackage'))"
      :button1-caption="yes"
      :button2-caption="no"
      :button2-visible="true"
      @confirmed="confirmPackageRetire"
      @cancelled="cancelPackageRetire" />

    <modal
      id="convert_deal_to_lineitems_confirmation_modal"
      :visible="isConvertDealToLineitemsModalVisible"
      :title="capitalize(i18n.global.t('actions.convertPackageToExternalDspLineitem'))"
      :body="capitalize(i18n.global.t('messages.confirmConvertPackageToExternalDspLineitem'))"
      :button1-caption="yes"
      :button2-caption="no"
      :button2-visible="true"
      @confirmed="confirmConvertDealToLineitems"
      @cancelled="cancelConvertDealToLineitems" />

    <audit-trail-modal
      :is-loading="isLoadingAuditTrail"
      :is-visible="isAuditTrailModalVisible"
      :audit-trail="auditTrail"
      :entity-description="deal.name"
      @confirmed="isAuditTrailModalVisible = false" />

    <modal
      id="modal_check_avails_unavailable"
      :visible="isCheckAvailsUnavailableModalVisible"
      :title="capitalize($t('actions.checkAvails'))"
      :body="capitalize($t('messages.checkAvailsIsNotAvailable'))"
      :button1-caption="capitalize($t('actions.ok'))"
      :button2-visible="false"
      @confirmed="isCheckAvailsUnavailableModalVisible = false" />

    <SitesFileDownload
      ref="sitesFileDownload"
      :name="deal.name"
      :download="(format) => exportTargetedSites(format)" />

    <UnitsFileDownload
      ref="unitsFileDownload"
      :name="deal.name"
      :download="(format) => exportTargetedUnits(format)" />
  </div>
</template>

<script setup lang="ts">
  //#region Imports

  import { DateTime } from 'luxon';
  import { capitalize, computed, markRaw, onBeforeMount, onMounted, onUnmounted, Ref, ref, watch } from 'vue';
  import { useRouter } from 'vue-router';

  import GoogleAnalyticsApi from '@/common/apis/GoogleAnalyticsApi';
  import { AuditTrailModal } from '@/common/components/audit';
  import AvailsResult from '@/common/components/AvailsResult.vue';
  import { PollenTargetingDetails } from '@/common/components/controls';
  import EntityActions from '@/common/components/controls/EntityActions.vue';
  import { LocationTargetingDetails } from '@/common/components/controls/targeting';
  import DealDetailsBudget from '@/common/components/deals/details/DealDetailsBudget.vue';
  import DealsDetailsChildDeals from '@/common/components/deals/details/DealDetailsChildDeals.vue';
  import DealDetailsCuratorRestrictions from '@/common/components/deals/details/DealDetailsCuratorRestrictions.vue';
  import DealDetailsCustomEventTargeting from '@/common/components/deals/details/DealDetailsCustomEventTargeting.vue';
  import DealDetailsCustomProperties from '@/common/components/deals/details/DealDetailsCustomProperties.vue';
  import DealDetailsDates from '@/common/components/deals/details/DealDetailsDates.vue';
  import DealDetailsDaypart from '@/common/components/deals/details/DealDetailsDaypart.vue';
  import DealDetailsDealID from '@/common/components/deals/details/DealDetailsDealID.vue';
  import DealDetailsDemographics from '@/common/components/deals/details/DealDetailsDemographics.vue';
  import DealDetailsDSPsAndBuyers from '@/common/components/deals/details/DealDetailsDSPsAndBuyers.vue';
  import DealDetailsExchanges from '@/common/components/deals/details/DealDetailsExchanges.vue';
  import DealDetailsExternalChildDeals from '@/common/components/deals/details/DealDetailsExternalChildDeals.vue';
  import DealDetailsFee from '@/common/components/deals/details/DealDetailsFee.vue';
  import DealDetailsGreenScore from '@/common/components/deals/details/DealDetailsGreenScore.vue';
  import DealDetailsKpis from '@/common/components/deals/details/DealDetailsKpis.vue';
  import DealDetailsLineItems from '@/common/components/deals/details/DealDetailsLineItems.vue';
  import DealDetailsMediaOwners from '@/common/components/deals/details/DealDetailsMediaOwners.vue';
  import DealDetailsMobileRetargeting from '@/common/components/deals/details/DealDetailsMobileRetargeting.vue';
  import DealDetailsNotes from '@/common/components/deals/details/DealDetailsNotes.vue';
  import DealDetailsPlayback from '@/common/components/deals/details/DealDetailsPlayback.vue';
  import DealDetailsSpendSteering from '@/common/components/deals/details/DealDetailsSpendSteering.vue';
  import DealDetailsSummary from '@/common/components/deals/details/DealDetailsSummary.vue';
  import DealDetailsWeatherTargeting from '@/common/components/deals/details/DealDetailsWeatherTargeting.vue';
  import TargetingSectionHelper from '@/common/components/entity-form/TargetingSectionHelper';
  import EntityIdBadge from '@/common/components/EntityIdBadge.vue';
  import { IconColoredDeal, IconColoredPrivateDeal } from '@/common/icons/colored';
  import {
    IconAudit,
    IconAvails,
    IconBuy,
    IconDuplicate,
    IconEdit,
    IconNext,
    IconPrevious,
    IconPublish,
    IconRetire,
    IconSale,
    IconScreen,
    IconSite,
    IconUnpublish,
    IconWarning,
  } from '@/common/icons/monochrome';
  import {
    FilterComparisonOperator,
    OrganizationCustomField,
    Package,
    PurchaseTypeOptions,
    TargetingCriterion,
  } from '@/common/models';
  import AuditTrail from '@/common/models/audit/AuditTrail';
  import CuratedDeliveryStatistics from '@/common/models/deliveryStatistics/CuratedDeliveryStatistics';
  import MediaOwner from '@/common/models/media-owner/MediaOwner';
  import CuratorOrganization from '@/common/models/misc-api-models/CuratorOrganization';
  import { CurationType } from '@/common/models/package/CurationType.enum';
  import PackageProperty from '@/common/models/package/PackageProperty';
  import PackageStatus from '@/common/models/package/PackageStatus.enum';
  import { FilterCollection, QueryParams } from '@/common/models/query-params';
  import { DASH } from '@/globals';
  import { Breadcrumbs } from '@/org-app/components/controls';
  import DealCreativesApproval from '@/org-app/components/creatives/approval/DealCreativesApproval.vue';
  import SitesFileDownload from '@/org-app/components/lineItems/SitesFileDownload.vue';
  import UnitsFileDownload from '@/org-app/components/lineItems/UnitsFileDownload.vue';
  import EntityCustomFieldsDetails from '@/org-app/components/organizationCustomFields/EntityCustomFieldsDetails.vue';
  import { useOrganizationDSPs } from '@/org-app/services/tanstack/queries';
  import { queryKeys } from '@/org-app/services/tanstack/queryKeys';
  import { getCachedData, waitForPendingQueries } from '@/org-app/services/tanstack/queryUtils';
  import { mapActions, mapGetters, mapOrgAppActions, mapOrgAppGetters, mapOrgAppState } from '@/utils/StateMapping';
  import i18n from '@/vueI18n';

  //#endregion

  defineExpose({
    closeModal,
  });

  //#region Props
  interface IDealDetailsProps {
    auditTrail?: AuditTrail;
    excludedLocations?: Location[];
    externalAvailsUrl: string;
    isAdDurationEnabled: boolean;
    isAuditTrailEnabled: boolean;
    isDaypartEnabled: boolean;
    isExchange?: boolean;
    showPackageBiddingMetrics: boolean;
    isUnitTargetingEnabled: boolean;
    locations?: Location[];
    organizationCustomFields: OrganizationCustomField[];
    organizationId: number;
    deal: Package;
    suppliers: MediaOwner[];
    targetingCriteria: TargetingCriterion[];
    pageSizes: { units: number };
    pageNumbers: { units: number };
    lengths: { units: number };
  }

  const props = withDefaults(defineProps<IDealDetailsProps>(), {
    externalAvailsUrl: '',
    isExchange: false,
    showPackageBiddingMetrics: false,
    organizationCustomFields: null,
    pageSizes: null,
    pageNumbers: null,
    lengths: null,
    auditTrail: null,
    locations: null,
    excludedLocations: null,
  });
  //#endregion

  //#region Data

  const { permissions } = mapGetters();
  const { organizationSettings, ReferenceData } = mapOrgAppState();
  const { isSupplyOrganization, isCuratorOrganization, isDemandOrganization } = mapOrgAppGetters();

  const router = useRouter();
  const breadcrumbs = ref(
    props.deal.isCuratedDeal
      ? [
          {
            name: capitalize(i18n.global.t('names.curatedDeal', { n: 2 })),
            url: '/curated-deals/',
          },
        ]
      : [
          {
            name: isCuratorOrganization.value
              ? capitalize(i18n.global.t('names.marketplace', { n: 1 }))
              : capitalize(i18n.global.t('names.deal', { n: 2 })),
            url: isCuratorOrganization.value ? '/curator-supplier-deals/' : '/deals/',
          },
        ],
  );
  const buyerOrganizationDSPsDescription = ref(DASH);
  const checkAvailsAlert = ref(null);
  const isAuditTrailModalVisible = ref(false);
  const isCheckAvailsUnavailableModalVisible = ref(false);
  const isConvertDealToLineitemsModalVisible = ref(false);
  const isEditPackageModalVisible = ref(false);
  const isLoadingAuditTrail = ref(false);
  const isPublishPackageModalVisible = ref(false);
  const isRetirePackageModalVisible = ref(false);
  const isUnPublishPackageModalVisible = ref(false);
  const planName = ref(null);
  const estimatedDeliveryStats: Ref<CuratedDeliveryStatistics> = ref(null);

  // Queries

  const organizationDSPsQuery = ref(null);

  // Refs

  const unitsFileDownload = ref(null);
  const sitesFileDownload = ref(null);
  const availsResult = ref(null);

  //#endregion

  //#region Computed

  const { isSidekickOrganization } = mapOrgAppGetters();
  const isSupplyOrCurateOrganization = computed(() => isSupplyOrganization.value || isCuratorOrganization.value);
  const curatorOrganizations = computed(
    () => ReferenceData?.curators?.map((curator: object) => CuratorOrganization.fromApi(curator)) || [],
  );
  const isRetired = computed(() => props.deal.state === PackageStatus.Retired);
  const no = computed(() => capitalize(i18n.global.t('actions.no')));
  const yes = computed(() => capitalize(i18n.global.t('actions.yes')));
  const isDaypartSectionVisible = computed(() => !isCuratorOrganization.value && props.isDaypartEnabled);
  const isAdDurationSectionVisible = computed(() => props.isAdDurationEnabled);
  const buyerOrganizationDSPs = computed(() => organizationDSPsQuery.value?.items ?? []);
  const packageBuyerOrganizationDSPs = computed(() =>
    buyerOrganizationDSPs.value.filter((x) => props.deal.buyerOrganizationDSPIDs.some((y) => y === x.id)),
  );
  const isExternalDeal = computed(() => {
    const externalDSPList = packageBuyerOrganizationDSPs.value.map((x) => x.dsp.isExternal);
    return externalDSPList.every((val) => val) && packageBuyerOrganizationDSPs.value.length > 0;
  });
  const targetingSectionHelper = computed(() => {
    return new TargetingSectionHelper(props.deal);
  });
  const hasWeatherTargeting = computed(() => {
    return targetingSectionHelper.value?.hasWeatherTargeting ?? false;
  });
  const hasPollenTargeting = computed(() => {
    return targetingSectionHelper.value?.hasPollenTargeting ?? false;
  });
  const isNGDeal = computed(() => props.deal?.isNGDeal);
  const isNGExternalOrPreferredExternalDeal = computed(() => props.deal?.isNGExternalOrPreferredExternalDeal);
  const mediaOwners = computed(() => {
    if (props.deal.organizationID) {
      return props.suppliers.filter((s) => s.id === props.deal.organizationID);
    }
    return props.suppliers.filter((s) => props.deal.mediaOwnerIDs.find((m) => m === s.id));
  });
  const entityCustomFields = computed(() =>
    props.organizationCustomFields == null || props.organizationCustomFields.length === 0 || props.deal == null
      ? []
      : props.organizationCustomFields.map((x) => {
          const existing = props.deal.packageProperties.find((y) => y.key === x.key);
          return existing ?? PackageProperty.fromApi(x.toApi());
        }),
  );
  const showCuratorRestrictions = computed(
    () =>
      props.deal.curatorOrganizationIDs && props.deal.curatorOrganizationIDs.length > 0 && isSupplyOrganization.value,
  );
  // In the case that it is a curated deal, we show the curator restrictions only if the deal is published
  const isExternalSSPCuratedDeal = computed(
    () => props.deal.isCuratedDeal && props.deal.curationType === CurationType.EXTERNAL_SSP_DEALS,
  );
  const showExternalExchanges = computed(
    () =>
      isNGExternalOrPreferredExternalDeal.value &&
      isSupplyOrCurateOrganization.value &&
      props.deal.buyerExternalExchangeIDs.length > 0,
  );
  const selectedCuratorOrganizations = computed(() =>
    curatorOrganizations.value.filter((x: CuratorOrganization) =>
      props.deal.curatorOrganizationIDs.includes(x.curatorOrganizationID),
    ),
  );
  const showLineItemsTable = computed(
    () =>
      !props.isExchange &&
      !isNGExternalOrPreferredExternalDeal.value &&
      isSupplyOrganization.value &&
      !props.deal.isPreferredOrNGInternalDeal,
  );
  const showSpendSteering = computed(
    () => props.suppliers.length > 0 && props.deal.isCuratedDeal && props.deal.spendSteering,
  );
  const hasInvalidConfiguration = computed(
    () => props.deal.hasIncompatibleRestrictions || props.deal.hasMismatchedPublisherDeals,
  );
  const showDealDetailsSummary = computed(() => {
    return (
      isSupplyOrganization.value || (isCuratorOrganization.value && props.deal.isCuratedDeal && props.deal.hasStarted)
    );
  });

  const actions = computed(() => {
    const actionEdit = {
      type: 'edit',
      name: capitalize(i18n.global.t('actions.edit')),
      icon: markRaw(IconEdit),
      $isDisabled: permissions.value == null || !permissions.value.canWritePackage,
    };

    const actionPublish = {
      type: 'publish',
      name: capitalize(i18n.global.t('actions.publish')),
      icon: markRaw(IconPublish),
      $isDisabled: permissions.value == null || !permissions.value.canWritePackage,
    };

    const actionRetire = {
      type: 'retire',
      name: capitalize(i18n.global.t('actions.retire')),
      icon: markRaw(IconRetire),
      $isDisabled: permissions.value == null || !permissions.value.canWritePackage,
    };

    const actionUnpublish = {
      type: 'unpublish',
      name: capitalize(i18n.global.t('actions.unpublish')),
      icon: markRaw(IconUnpublish),
      $isDisabled: permissions.value == null || !permissions.value.canWritePackage,
    };

    const exportTargetedSitesAction = {
      type: 'exportTargetedSites',
      name: capitalize(i18n.global.t('actions.downloadTargetedSites')),
      icon: markRaw(IconSite),
    };
    const exportTargetedUnitsAction = {
      type: 'exportTargetedUnits',
      name: capitalize(i18n.global.t('actions.downloadTargetedScreens')),
      icon: markRaw(IconScreen),
    };

    const actionConvertToExternalDSPLineItems = {
      type: 'convert-deal-to-external-dsp-lineitems',
      name: capitalize(i18n.global.t('actions.convertPackageToExternalDspLineitem')),
      icon: markRaw(IconSale),
      $isDisabled: permissions.value == null || !permissions.value.canWriteLineitem,
    };

    const actionPreviousRevision = {
      type: 'previous-revision',
      name: capitalize(i18n.global.t('actions.seePreviousRevision')),
      icon: markRaw(IconPrevious),
    };

    const actionNextRevision = {
      type: 'next-revision',
      name: capitalize(i18n.global.t('actions.seeNextRevision')),
      icon: markRaw(IconNext),
    };

    const checkAvailsLabel = organizationSettings?.uiEnablePredictiveAvails
      ? `${capitalize(
          i18n.global.t('actions.checkAvailability'),
        )} <span class="ml-2 badge badge-xs badge-info">${capitalize(i18n.global.t('messages.experimental'))} </span>`
      : capitalize(i18n.global.t('actions.checkAvailability'));
    const actionCheckAvails = {
      type: 'checkavails',
      name: checkAvailsLabel,
      icon: markRaw(IconAvails),
      $isDisabled: permissions.value == null || !permissions.value.canReadAvails,
    };

    const actionDuplicate = {
      type: 'duplicate',
      name: capitalize(i18n.global.t('actions.createCopy')),
      icon: markRaw(IconDuplicate),
    };

    const actionBuy = {
      type: 'buy',
      name: capitalize(i18n.global.t('actions.buy')),
      icon: markRaw(IconBuy),
      $isDisabled: permissions.value == null || !permissions.value.canWriteLineitem,
    };

    const actionShowAudit = {
      type: 'show-audit',
      name: capitalize(i18n.global.t('actions.viewAuditTrail')),
      icon: markRaw(IconAudit),
      $isDisabled: permissions.value == null || !permissions.value.canReadAuditpackage,
    };

    const demandOrganizationActions = [
      actionBuy,
      ...(organizationSettings?.uiEnableLineItemUnitTargeting ? [exportTargetedUnitsAction] : []),
    ];
    const supplyOrganizationActions = [
      ...(props.deal.state !== PackageStatus.Retired ? [actionEdit] : []),
      ...(props.deal.state === PackageStatus.Draft ? [actionPublish] : []),
      ...(props.deal.state === PackageStatus.Published ? [actionUnpublish] : []),
      ...(!isRetired.value ? [actionRetire] : []),
      exportTargetedSitesAction,
      exportTargetedUnitsAction,
      ...(isExternalDeal.value &&
      !isNGExternalOrPreferredExternalDeal.value &&
      props.deal.state === PackageStatus.Published
        ? [actionConvertToExternalDSPLineItems]
        : []),
      ...(props.deal.revision > 0 ? [actionPreviousRevision] : []),
      ...(props.deal.revisedByID != null ? [actionNextRevision] : []),
      ...(!isRetired.value ? [actionCheckAvails] : []),
      actionDuplicate,
      ...(props.isAuditTrailEnabled ? [actionShowAudit] : []),
    ];
    const curatorOrganizationActions = [
      ...(props.deal.isCuratedDeal && props.deal.state !== PackageStatus.Retired ? [actionEdit] : []),
      ...(props.deal.isCuratedDeal && props.deal.state === PackageStatus.Draft ? [actionPublish] : []),
      ...(props.deal.isCuratedDeal && props.deal.state === PackageStatus.Published ? [actionUnpublish] : []),
      ...(props.deal.isCuratedDeal && !isRetired.value ? [actionRetire] : []),
      ...(props.deal.revision > 0 ? [actionPreviousRevision] : []),
      ...(props.deal.isCuratedDeal ? [exportTargetedUnitsAction] : []),
      ...(props.deal.revisedByID != null ? [actionNextRevision] : []),
      ...(props.deal.isCuratedDeal ? [actionDuplicate] : []),
      ...(props.isAuditTrailEnabled ? [actionShowAudit] : []),
    ];
    const exchangeActions = [
      ...(props.deal.state !== PackageStatus.Retired ? [actionEdit] : []),
      ...(props.deal.state !== PackageStatus.Draft ? [actionPublish] : []),
      ...(props.deal.state !== PackageStatus.Published ? [actionUnpublish] : []),
      ...(!isRetired.value ? [actionRetire] : []),
      exportTargetedSitesAction,
      exportTargetedUnitsAction,
      ...(isExternalDeal.value &&
      !isNGExternalOrPreferredExternalDeal.value &&
      props.deal.state === PackageStatus.Published
        ? [actionConvertToExternalDSPLineItems]
        : []),
      ...(props.deal.revision > 0 ? [actionPreviousRevision] : []),
      ...(props.deal.revisedByID != null ? [actionNextRevision] : []),
      ...(!isRetired.value ? [actionCheckAvails] : []),
      ...(props.isAuditTrailEnabled ? [actionShowAudit] : []),
    ];
    if (isSidekickOrganization.value) return [];
    if (isCuratorOrganization.value) return curatorOrganizationActions;
    if (isDemandOrganization.value) return demandOrganizationActions;
    if (isSupplyOrganization.value) return supplyOrganizationActions;
    else return exchangeActions;
  });

  //#endregion

  //#region Functions

  const { updatePackageState } = mapActions();

  const {
    getCuratedPackageEstimatedDeliveryStatistics,
    getDownloadTargetedSites,
    getDownloadTargetedScreens,
    getPlan,
    getCurators,
    setPackageSummaryDateRange,
  } = mapOrgAppActions();

  function edit() {
    router.push(props.deal.editEndpoint).catch(() => {
      // Do nothing
    });
  }

  function publish() {
    promptForPackagePublish();
  }

  function unpublish() {
    promptForPackageUnpublish();
  }

  function retire() {
    promptForPackageRetire();
  }

  function buy() {
    router.push(props.deal.buyEndpoint).catch(() => {
      // Do nothing
    });
  }

  function convertDealToExternalDspLineitems() {
    promptForConvertingDealToLineitems();
  }

  function confirmEditPackage() {
    isEditPackageModalVisible.value = false;
    edit();
  }

  function cancelEditPackage() {
    isEditPackageModalVisible.value = false;
  }

  function promptForPackagePublish() {
    isPublishPackageModalVisible.value = true;
  }

  function confirmPackagePublish() {
    updatePackageState({
      package: props.deal,
      value: PackageStatus.Published,
    });
    emit('publishPackage', props.deal);
  }

  function cancelPackagePublish() {
    isPublishPackageModalVisible.value = false;
  }

  function promptForPackageUnpublish() {
    isUnPublishPackageModalVisible.value = true;
  }

  function confirmPackageUnpublish() {
    updatePackageState({
      package: props.deal,
      value: PackageStatus.Draft,
    });
    emit('unpublishPackage', props.deal);
  }

  function cancelPackageUnpublish() {
    isUnPublishPackageModalVisible.value = false;
  }

  function promptForPackageRetire() {
    isRetirePackageModalVisible.value = true;
  }

  function confirmPackageRetire() {
    updatePackageState({
      package: props.deal,
      value: PackageStatus.Retired,
    });
    emit('retirePackage', props.deal);
  }

  function cancelPackageRetire() {
    isRetirePackageModalVisible.value = false;
  }

  function promptForConvertingDealToLineitems() {
    isConvertDealToLineitemsModalVisible.value = true;
  }

  const emit = defineEmits<{
    convertToLineItem: [deal: Package];
    getAuditTrail: [];
    previousRevision: [];
    nextRevision: [];
    publishPackage: [deal: Package];
    unpublishPackage: [deal: Package];
    retirePackage: [deal: Package];
  }>();

  function confirmConvertDealToLineitems() {
    emit('convertToLineItem', props.deal);
  }

  function cancelConvertDealToLineitems() {
    isConvertDealToLineitemsModalVisible.value = false;
  }

  function updateBuyerOrganizationDSPsDescription() {
    buyerOrganizationDSPsDescription.value = packageBuyerOrganizationDSPs.value.map((x) => x.name).join(', ');
  }

  function getPurchaseTypeOptionFromValue(val) {
    return PurchaseTypeOptions.options(true, true).filter((x) => x.value === val)[0].name;
  }

  function getAuditTrail() {
    if (!props.isAuditTrailEnabled) {
      return;
    }

    isLoadingAuditTrail.value = true;
    emit('getAuditTrail');
  }

  function seePreviousRevision() {
    emit('previousRevision');
  }

  function seeNextRevision() {
    emit('nextRevision');
  }

  async function exportTargetedSites(format = null) {
    if (format != null) {
      return getDownloadTargetedSites({
        format,
        type: 'packages',
        id: props.deal.id,
      });
    }
    sitesFileDownload.value.show();
    return null;
  }

  async function exportTargetedUnits(format = null) {
    if (format != null) {
      return getDownloadTargetedScreens({
        format,
        type: 'packages',
        id: props.deal.id,
      });
    }
    unitsFileDownload.value.show();
    return null;
  }

  function closeModal() {
    isAuditTrailModalVisible.value = false;
    isCheckAvailsUnavailableModalVisible.value = false;
    isConvertDealToLineitemsModalVisible.value = false;
    isEditPackageModalVisible.value = false;
    isPublishPackageModalVisible.value = false;
    isRetirePackageModalVisible.value = false;
    isUnPublishPackageModalVisible.value = false;
  }

  async function fetchPlan() {
    const result = await getPlan({ id: props.deal.planID });
    if (result) {
      planName.value = result.name;
    }
  }

  function dispatchAction(action) {
    const value = `${props.deal.id} - ${props.deal.name}`;

    GoogleAnalyticsApi.sendEvent('Packages', { category: capitalize(action.type), value });

    switch (action.type) {
      case 'edit':
        edit();
        break;
      case 'publish':
        publish();
        break;
      case 'unpublish':
        unpublish();
        break;
      case 'retire':
        retire();
        break;
      case 'buy':
        buy();
        break;
      case 'convert-deal-to-external-dsp-lineitems':
        convertDealToExternalDspLineitems();
        break;
      case 'show-audit':
        isAuditTrailModalVisible.value = true;
        break;
      case 'previous-revision':
        seePreviousRevision();
        break;
      case 'next-revision':
        seeNextRevision();
        break;
      case 'checkavails':
        if (props.deal.startDate < DateTime.local()) {
          checkAvailsAlert.value = i18n.global.t('messages.dealCheckAvailsNotAvailableBecauseStartDateIsInThePast');
          return;
        }
        checkAvailsAlert.value = null;
        if (props.externalAvailsUrl == null || props.externalAvailsUrl.trim() === '') {
          isCheckAvailsUnavailableModalVisible.value = true;
          return;
        }
        availsResult.value?.checkAvails();
        break;
      case 'exportTargetedSites':
        exportTargetedSites();
        break;
      case 'exportTargetedUnits':
        exportTargetedUnits();
        break;
      case 'duplicate': {
        const routePrefix = props.deal.isCuratedDeal ? 'curate' : 'deals';
        router.push(`/${routePrefix}/new/edit/duplicate/${props.deal.id}`).catch(() => {
          // Do nothing
        });
        break;
      }
      default:
        break;
    }
  }

  //#endregion

  //#region Watchers

  watch(
    () => props.auditTrail,
    () => {
      isLoadingAuditTrail.value = false;
    },
  );

  watch(
    () => isAuditTrailModalVisible.value,
    () => {
      if (props.auditTrail === null) {
        getAuditTrail();
      }
    },
  );

  //#endregion

  //#region Lifecycle

  // Makes sure that this date range value is cleared when the component is mounted/unmounted
  onBeforeMount(() => {
    setPackageSummaryDateRange(null);
  });

  onUnmounted(() => {
    setPackageSummaryDateRange(null);
  });

  onMounted(async () => {
    if (
      isSupplyOrCurateOrganization.value &&
      !showExternalExchanges.value &&
      !showCuratorRestrictions.value &&
      props.deal.buyerOrganizationDSPIDs.length > 0
    ) {
      // If we already have all organization DSPs, we don't need to make a filtered query
      const cachedData = getCachedData([queryKeys.organizationDSPs]);
      organizationDSPsQuery.value = useOrganizationDSPs(
        undefined,
        !cachedData
          ? {
              queryParams: QueryParams.all(
                FilterCollection.for(
                  'organization_dsp_id',
                  props.deal.buyerOrganizationDSPIDs,
                  FilterComparisonOperator.IN,
                ),
              ),
            }
          : {},
      );
      await waitForPendingQueries([organizationDSPsQuery.value]);
    }

    if (props.deal?.planID && organizationSettings?.enablePlansFeature) {
      fetchPlan();
    }

    if (isSupplyOrganization.value) {
      updateBuyerOrganizationDSPsDescription();
      if (curatorOrganizations.value === null || curatorOrganizations.value.length === 0) {
        await getCurators();
      }
    } else if (isCuratorOrganization.value) {
      if (props.deal?.packageIDs?.length > 0) {
        // Get estimated delivery stats for curated package, the result includes
        // child deal stats which are displayed in DealDetailsChildDeals.vue
        estimatedDeliveryStats.value = await getCuratedPackageEstimatedDeliveryStatistics({
          params: props.deal?.targetParamsEstimatedDeliveryStatistics,
        });
      }
    }

    breadcrumbs.value.push({
      name: props.deal.name,
      url: null,
    });
    window.document.title = `${window.document.title}: ${props.deal.name}`;
  });

  //#endregion
</script>

<style lang="scss" rel="stylesheet/scss">
  #app {
    #package_details {
      .avails-result {
        margin-top: 1rem;
      }

      .alert {
        display: flex;
        align-items: center;

        svg {
          width: 3.2rem;
          height: 3.2rem;
          margin-right: 1.6rem;
        }
      }

      .chart-container {
        height: 30rem;
        display: flex;
        flex-direction: column;
        align-items: center;
        position: relative;
        .echarts {
          margin-bottom: 0rem;
        }
      }
    }
  }
</style>
